import { BrowserRouter, Routes, Route, Link, Navigate } from "react-router-dom";
import Home from "./pages/Home";
import NigeriaProducts from "./pages/NigeriaProducts";
import Cart from "./pages/Cart";
import About from "./pages/About";
import Careers from "./pages/Careers";
import Products from "./pages/Products";
import SearchResults from "./pages/SearchResults";
import { CartProvider, useCart, } from "./context/CartContext";
import "./styles.css";
import Contact from "./pages/Contact";
import Footer from "./components/Footer";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Checkout from "./pages/Checkout";
import OrderSuccess from "./pages/OrderSuccess";
import OrderCancel from "./pages/OrderCancel";

import ProductDetail from "./pages/ProductDetail";
import OrderLookup from "./pages/OrderLookup";
import UserOrders from "./pages/UserOrders";

import AdminOrders from "./pages/admin/AdminOrders";
import AdminOrderDetail from "./pages/admin/AdminOrderDetail";
import AddNewProducts from "./pages/admin/AddNewProducts";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminEditProduct from "./pages/admin/AdminEditProduct";
import AdminDashboard from "./pages/admin/AdminDashboard";
import ResetPassword from "./pages/ResetPassword";
import UpdatePassword from "./pages/UpdatePassword";
import { AdminRoute } from "./components/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import ReceiptPage from "./pages/ReceiptPage";
import { CountryProvider } from "./context/CountryContext";
import ProfileMenu from "./components/ProfileMenu";

import logo from "./assets/logo.png";
import { useEffect, useState } from "react";
import { SearchProvider, useSearch } from "./context/SearchContext";
import { useNavigate } from "react-router-dom";
import { supabase } from "./utils/supabaseClient";
import "./styles/Theme.css";

function Navbar() {
  const { cart } = useCart();
  const { user, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const count = cart.reduce((sum, item) => sum + item.qty, 0);
  const { searchTerm, setSearchTerm } = useSearch();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    async function loadProfile() {
      if (!user) { setProfile(null); return; }
      const { data } = await supabase
        .from("profiles")
        .select("first_name, last_name, is_admin")
        .eq("id", user.id)
        .single();
      setProfile(data);
    }
    loadProfile();
  }, [user]);

  return (
    <nav className="nav sticky">
      {/* Logo */}
      <Link to="/" className="nav-logo">
        <img src={logo} alt="Aquaviva Logo" />
        <span>Aquaviva Foods</span>
      </Link>

      {/* Search Bar */}
      <div className="nav-search">
        <input
          type="text"
          placeholder="Search fresh produce..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              navigate("/search");
            }
          }}
        />
        <button
          className="search-btn"
          onClick={() => navigate("/search")}
        >
        🔍
        </button>
      </div>

      {/* Desktop Links */}
      <div className={`nav-links ${menuOpen ? "open" : ""}`}>     
        <Link to="/">Home </Link>

        {/* PRODUCTS DROPDOWN */}
        <div className="dropdown">
          <span className="dropdown-title">Products ▾</span>
          <div className="dropdown-menu">
            <Link to="/products">Our Products</Link>
            <Link to="/products/grains">Grains & Flours</Link>
            <Link to="/products/legumes">Legumes</Link>
            <Link to="/products/spices">Spices & Seasonings</Link>
            <Link to="/products/dry-fish">Dry Fish & Seafood</Link>
          </div>
        </div>

        {/* ABOUT US DROPDOWN */}
        <div className="dropdown">
          <span className="dropdown-title">About Us ▾</span>
          <div className="dropdown-menu">
            <Link to="/about">Company</Link>
            <Link to="/careers">Careers</Link>
          </div>
        </div>

        <Link to="/contact">Contact Us </Link>
        {profile?.is_admin && (
          <Link to="/admin" className="nav-link">Dashboard</Link>
        )}

        {profile && !profile.is_admin && (
          <Link to="/account/orders" className="nav-link">My Orders</Link>
        )}

        {/* Cart Icon */}
        <Link to="/cart" className="cart-icon">
          🛒
          {count > 0 && <span className="cart-badge">{count}</span>}
        </Link>
      
        {/* Auth */}
        {user ? (
          <ProfileMenu user={user} profile={profile} onSignOut={signOut} />
        ) : (
          <div className="auth-links">
            <Link to="/login">Login</Link>
            <span> | </span>
            <Link to="/signup">Register</Link>
          </div>
        )}
      </div>

      {/* Hamburger Menu */}
      <div
        className={`hamburger ${menuOpen ? "active" : ""}`}
        onClick={() => setMenuOpen(!menuOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </div>
    </nav>
  );
}

export default function App() {
  return (
    <SearchProvider>
      <CountryProvider>
        <CartProvider>
          <BrowserRouter>
            <Navbar />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/nigeria" element={<NigeriaProducts />} />

              {/* Products */}
              <Route path="/products" element={<Products />} />
              
              <Route path="/search" element={<SearchResults />} />
              <Route path="/products/:category" element={<Products />} />
              <Route path="/product/:id" element={<ProductDetail />} />

              {/* Company */}
              <Route path="/about" element={<About />} />
              <Route path="/careers" element={<Careers />} />
              <Route path="/contact" element={<Contact />} />

              {/* Auth */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/update-password" element={<UpdatePassword />} />
              
              {/* Admin */}
              <Route
                path="/admin/products/new"
                element={
                  <AdminRoute>
                    <AddNewProducts />
                  </AdminRoute>
                }
              />
              <Route path="/admin/products" element={<AdminProducts />} />
              <Route path="/admin/products/edit/:id" element={<AdminEditProduct />} />
              <Route path="/admin" 
                element={
                  <AdminRoute>
                    <AdminDashboard />
                  </AdminRoute>
                } 
              />
              {/* Admin Order */}
              <Route path="/admin/orders/:id" element={<AdminOrderDetail />} />
              <Route path="/admin/orders" element={<AdminOrders />} />
              <Route path="/orders" element={<Navigate to="/account/orders" replace />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/order-success/:orderId" element={<OrderSuccess />} />
              <Route path="/order-cancel" element={<OrderCancel />} />
              <Route path="/order-lookup" element={<OrderLookup />} />

              <Route path="/account/orders" element={<UserOrders />} />
              <Route path="/receipt/:orderId" element={<ReceiptPage />} />

              {/* Cart */}
              <Route path="/cart" element={<Cart />} />
            </Routes>        
            <Footer />
          </BrowserRouter>
        </CartProvider>
      </CountryProvider>
    </SearchProvider>
  );
}